import {
  Button,
  Calendar,
  Clock,
  Mail,
  MapPin,
  Phone,
  User,
  formatPhoneNumberForDisplay,
} from "@ews-admin/global-design-system";
import React from "react";
import { useTranslation } from "react-i18next";

import {
  Appointment,
  AppointmentStatusEnum,
} from "../../../types/appointment.definition";
import { convertToDateAndTime } from "../../../utils/date.utils";
import { StatusBadge } from "../StatusBadge/StatusBadge";

interface BookedAppointmentCardProps {
  appointment: Appointment;
  onViewPatient: () => void;
  onCancel: () => void;
}

const computeAge = (dateOfBirth?: string): number | null => {
  if (!dateOfBirth) return null;
  const birth = new Date(dateOfBirth);
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const monthDiff = now.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age;
};

/**
 * Read-only appointment card shown for booked slots that cannot be deleted,
 * mirroring the medecine360-ui doctor/schedule appointment card layout.
 */
export const BookedAppointmentCard: React.FC<BookedAppointmentCardProps> = ({
  appointment,
  onViewPatient,
  onCancel,
}) => {
  const { t } = useTranslation();
  const patient = appointment.patient;
  const { date, time } = convertToDateAndTime(appointment.appointmentTime);
  const age = computeAge(patient?.dateOfBirth);
  const civilityLabel = patient?.civility
    ? t(`availability.civility.${patient.civility}`, patient.civility)
    : "";
  const bookedBy = appointment.bookedBy;
  const canCancel =
    appointment.status !== AppointmentStatusEnum.CANCELED &&
    appointment.status !== AppointmentStatusEnum.COMPLETE;

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <div className="flex flex-col">
        <h3 className="text-lg font-semibold text-gray-900">
          {patient?.firstName} {patient?.lastName}
        </h3>
        <div className="mb-4 mt-1">
          <StatusBadge status={appointment.status} />
        </div>

        <div className="mb-4 grid grid-cols-2 gap-4">
          <div className="flex items-center text-sm text-gray-500">
            <Calendar className="mr-2 h-4 w-4" />
            {date}
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <Clock className="mr-2 h-4 w-4" />
            {time}
          </div>
          {(age !== null || civilityLabel) && (
            <div className="flex items-center text-sm text-gray-500">
              <User className="mr-2 h-4 w-4" />
              {age !== null ? `${age} ${t("availability.yearsShort")} ` : ""}
              {civilityLabel}
            </div>
          )}
          {appointment.location?.address && (
            <div className="flex items-center text-sm text-gray-500">
              <MapPin className="mr-2 h-4 w-4" />
              {appointment.location.address}
            </div>
          )}
          <div className="flex items-center text-sm text-gray-500">
            <Phone className="mr-2 h-4 w-4" />
            {patient?.phoneNumber
              ? formatPhoneNumberForDisplay(patient.phoneNumber)
              : t("availability.noPhone")}
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <Mail className="mr-2 h-4 w-4" />
            {patient?.email || t("availability.noEmail")}
          </div>
          {bookedBy && (
            <div className="flex items-center text-sm text-gray-500">
              <User className="mr-2 h-4 w-4" />
              <span>
                <span className="font-medium">
                  {t("availability.bookedByLabel")}:
                </span>{" "}
                {bookedBy.firstName} {bookedBy.lastName}
                {bookedBy.roleName && (
                  <span className="ml-1 text-xs text-gray-400">
                    ({bookedBy.roleName})
                  </span>
                )}
              </span>
            </div>
          )}
          {appointment.reason && (
            <div className="col-span-2 mt-2 flex items-start text-sm text-gray-500">
              <span className="mr-2 font-medium">
                {t("availability.reasonLabel")}:
              </span>
              <span>{appointment.reason}</span>
            </div>
          )}
        </div>

        <div className="mt-auto flex w-full flex-col space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={onViewPatient}
            className="w-full sm:w-auto"
          >
            {t("availability.viewPatient")}
          </Button>
          {canCancel && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-red-600 hover:text-red-700 sm:w-auto"
              onClick={onCancel}
            >
              {t("availability.cancelAppointment")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookedAppointmentCard;
